import React from 'react';
import { Box, Typography, Grid, Paper, Switch, FormControlLabel, Divider, TextField, Button, Select, MenuItem, FormControl, InputLabel } from '@mui/material';

const SettingsPage: React.FC = () => {
  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Settings
      </Typography>
      
      <Grid container spacing={4}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, mb: 4 }}>
            <Typography variant="h5" gutterBottom>
              General Settings
            </Typography>
            <Divider sx={{ mb: 3 }} />
            
            <Box sx={{ mb: 3 }}>
              <FormControlLabel
                control={<Switch defaultChecked />}
                label="Enable AI-Powered Insights"
              />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, ml: 4 }}>
                Allow Bruno AI to automatically generate insights from your financial data
              </Typography>
            </Box>
            
            <Box sx={{ mb: 3 }}>
              <FormControlLabel
                control={<Switch defaultChecked />}
                label="Real-time Analytics"
              />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, ml: 4 }}>
                Update analytics dashboards in real-time as data changes
              </Typography>
            </Box>
            
            <Box sx={{ mb: 3 }}>
              <FormControlLabel
                control={<Switch defaultChecked />}
                label="Email Notifications"
              />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, ml: 4 }}>
                Receive email notifications for important financial insights
              </Typography>
            </Box>
            
            <Box sx={{ mb: 3 }}>
              <FormControlLabel
                control={<Switch />}
                label="Dark Mode"
              />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, ml: 4 }}>
                Switch to dark theme
              </Typography>
            </Box>
          </Paper>
          
          <Paper sx={{ p: 3 }}>
            <Typography variant="h5" gutterBottom>
              Data Settings
            </Typography>
            <Divider sx={{ mb: 3 }} />
            
            <Box sx={{ mb: 3 }}>
              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel id="currency-label">Default Currency</InputLabel>
                <Select
                  labelId="currency-label"
                  id="currency-select"
                  defaultValue="USD"
                  label="Default Currency"
                >
                  <MenuItem value="USD">USD ($)</MenuItem>
                  <MenuItem value="EUR">EUR (€)</MenuItem>
                  <MenuItem value="GBP">GBP (£)</MenuItem>
                  <MenuItem value="JPY">JPY (¥)</MenuItem>
                  <MenuItem value="CAD">CAD ($)</MenuItem>
                </Select>
              </FormControl>
              
              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel id="date-format-label">Date Format</InputLabel>
                <Select
                  labelId="date-format-label"
                  id="date-format-select"
                  defaultValue="MM/DD/YYYY"
                  label="Date Format"
                >
                  <MenuItem value="MM/DD/YYYY">MM/DD/YYYY</MenuItem>
                  <MenuItem value="DD/MM/YYYY">DD/MM/YYYY</MenuItem>
                  <MenuItem value="YYYY-MM-DD">YYYY-MM-DD</MenuItem>
                </Select>
              </FormControl>
              
              <FormControl fullWidth>
                <InputLabel id="timezone-label">Time Zone</InputLabel>
                <Select
                  labelId="timezone-label"
                  id="timezone-select"
                  defaultValue="UTC-5"
                  label="Time Zone"
                >
                  <MenuItem value="UTC-8">Pacific Time (UTC-8)</MenuItem>
                  <MenuItem value="UTC-7">Mountain Time (UTC-7)</MenuItem>
                  <MenuItem value="UTC-6">Central Time (UTC-6)</MenuItem>
                  <MenuItem value="UTC-5">Eastern Time (UTC-5)</MenuItem>
                  <MenuItem value="UTC+0">UTC</MenuItem>
                  <MenuItem value="UTC+1">Central European Time (UTC+1)</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Paper>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, mb: 4 }}>
            <Typography variant="h5" gutterBottom>
              API Settings
            </Typography>
            <Divider sx={{ mb: 3 }} />
            
            <TextField
              fullWidth
              label="API Key"
              variant="outlined"
              defaultValue="sk_test_51N8D******************"
              type="password"
              sx={{ mb: 3 }}
            />
            
            <Box sx={{ display: 'flex', mb: 3 }}>
              <Button variant="outlined" sx={{ mr: 2 }}>
                Regenerate API Key
              </Button>
              <Button variant="outlined">
                Copy API Key
              </Button>
            </Box>
            
            <Typography variant="body2" color="text.secondary">
              This API key provides full access to the Bruno AI API. Protect it like a password.
            </Typography>
          </Paper>
          
          <Paper sx={{ p: 3 }}>
            <Typography variant="h5" gutterBottom>
              Model & AI Settings
            </Typography>
            <Divider sx={{ mb: 3 }} />
            
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel id="openai-model-label">OpenAI Model</InputLabel>
              <Select
                labelId="openai-model-label"
                id="openai-model-select"
                defaultValue="gpt-4"
                label="OpenAI Model"
              >
                <MenuItem value="gpt-4">GPT-4</MenuItem>
                <MenuItem value="gpt-4-turbo">GPT-4 Turbo</MenuItem>
                <MenuItem value="gpt-3.5-turbo">GPT-3.5 Turbo</MenuItem>
              </Select>
            </FormControl>
            
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel id="vector-db-label">Vector Database</InputLabel>
              <Select
                labelId="vector-db-label"
                id="vector-db-select"
                defaultValue="weaviate"
                label="Vector Database"
              >
                <MenuItem value="weaviate">Weaviate</MenuItem>
                <MenuItem value="pinecone">Pinecone</MenuItem>
                <MenuItem value="qdrant">Qdrant</MenuItem>
              </Select>
            </FormControl>
            
            <TextField
              fullWidth
              label="Vector Database URL"
              variant="outlined"
              defaultValue="ce0a6tdeqlk29eflzojvdg.c0.us-west3.gcp.weaviate.cloud"
              sx={{ mb: 3 }}
            />
            
            <TextField
              fullWidth
              label="Vector Database API Key"
              variant="outlined"
              defaultValue="p7ApQyttMJj9VbrtGHx2FJDxJqkw6MML84r6"
              type="password"
            />
          </Paper>
        </Grid>
      </Grid>
      
      <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="outlined" sx={{ mr: 2 }}>
          Cancel
        </Button>
        <Button variant="contained">
          Save Changes
        </Button>
      </Box>
    </Box>
  );
};

export default SettingsPage;
